import {
  afterRender,
  Component,
  HostListener,
  Input,
  linkedSignal,
  model,
  OnInit,
  signal,
} from '@angular/core';
import { BOProduct, BreakpointNumeric, Images } from '@boels/models';
import { Observable, take } from 'rxjs';
import { GalleriaModule } from 'primeng/galleria';
import { normalizeImages } from '@boels/helpers';
import { ImageModule } from 'primeng/image';

interface GalleriaImage {
  itemImageSrc: string;
  thumbnailImageSrc: string;
  zoomImageSrc: string;
  alt: string;
  title: string;
}

interface ResponsiveOption {
  breakpoint: string;
  numVisible: number;
}

@Component({
  selector: 'boels-product-images',
  templateUrl: './product-images.component.html',
  styleUrls: ['./product-images.component.scss'],
  imports: [GalleriaModule, ImageModule],
  host: { ngSkipHydration: 'true' },
})
export class ProductImagesComponent implements OnInit {
  @Input() componentData: { product$: Observable<BOProduct> };

  protected readonly $galleryImages = signal<GalleriaImage[]>([]);
  protected readonly $zoomVisible = signal(false);
  protected readonly $activeIndex = model(0);
  protected readonly $zoomActiveIndex = linkedSignal(() => this.$activeIndex());

  protected readonly responsiveOptions: ResponsiveOption[] = Object.keys(
    BreakpointNumeric,
  ).map((key) => {
    const breakpoint = BreakpointNumeric[
      key as keyof typeof BreakpointNumeric
    ] as number;
    return {
      breakpoint: `${breakpoint}px`,
      numVisible:
        breakpoint >= 768 && breakpoint <= 1024
          ? 4
          : breakpoint >= 1024
            ? 8
            : 3,
    };
  });

  constructor() {
    afterRender(() => {
      if (this.$zoomVisible()) {
        const mask = document.getElementsByClassName('p-galleria-mask')[0];
        if (mask) mask.addEventListener('click', this.maskClickHandler);
      }
    });
  }

  @HostListener('document:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.$zoomVisible()) {
      this.closeZoomModal();
    }
  }

  ngOnInit(): void {
    this.componentData.product$.pipe(take(1)).subscribe((product) => {
      const images: Images[] = product.variantOptions
        ? product.baseProductImages
        : product.images;
      const normalizedImages = normalizeImages(images);

      const galleryImages =
        normalizedImages['GALLERY'] &&
        (<any[]>normalizedImages['GALLERY']).map((image) => {
          const galleriaImage: GalleriaImage = {
            alt: image.store.altText,
            itemImageSrc: image.store.url,
            thumbnailImageSrc: image.thumbnail.url,
            title: image.store.altText,
            zoomImageSrc: image.zoom?.url || image.store.url,
          };
          return galleriaImage;
        });

      this.$galleryImages.set(galleryImages);
    });
  }

  openZoomModal() {
    this.$zoomVisible.set(true);
  }

  closeZoomModal() {
    const mask = document.getElementsByClassName('p-galleria-mask')[0];
    if (mask) mask.removeEventListener('click', this.maskClickHandler);

    this.$zoomVisible.set(false);
    this.$activeIndex.set(this.$zoomActiveIndex());
  }

  private maskClickHandler = (event: Event) => {
    const content = document.getElementsByClassName('p-galleria-fullscreen')[0];
    if (content && !content.contains(event.target as Node))
      this.closeZoomModal();
  };
}
